import { trackEvent } from '@zep/logger/tracking';

import { type AiBuilderUsageInfo } from '../stores/types';

import {
  resolveTurnFailureReason,
  trackTurnResult,
  trackTurnStart,
  trackTurnStreamEvent,
} from './aiBuilderTurnTracking';
import { createAppliedGenerationCounter } from './aiGenerationCompletedTracking';
import {
  type AiBuilderAuthExpiredPlace,
  type AiBuilderChatRestoreResult,
  type AiBuilderChatSnapshotSkipReason,
  type AiBuilderUsageBlockedPlace,
} from './events.quiz-ai-builder';
import {
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderTurnRequest,
} from './types';

type AiBuilderTurnEventStream =
  | Iterable<AiBuilderChatOrchestrationEvent>
  | AsyncIterable<AiBuilderChatOrchestrationEvent>;

/**
 * 서버 턴 orchestration event stream 을 통과시키며 분석 이벤트를 발화하는 패스스루 래퍼.
 * 스트림 내용/순서는 변경하지 않는다 (behavior 무변경).
 *
 * - turn_start / turn_end / turn_error / turn_abort: Turn Success Rate
 * - question_change: Applied Operation / Undo Rate (change_type='undo')
 * - choice_view: Repeated Clarification Rate
 * - biz_ai_generation_completed: 구 모달 플로우와의 pre/post 브리지.
 *   스트림 중간이 아니라 턴 종료 시 generation_id 별 1회 발화한다 — 적용(apply)이
 *   이미 일어난 generation 은 턴이 중단/실패로 끝나도 완료로 집계한다.
 */
export async function* trackAiBuilderTurnAnalyticsEvents(
  events: AiBuilderTurnEventStream,
  request: AiBuilderTurnRequest,
): AsyncIterable<AiBuilderChatOrchestrationEvent> {
  const startedAt = Date.now();
  const { clientRequestId } = request;
  const aborted = () => request.context.signal?.aborted ?? false;
  const started = trackTurnStart(request);
  const { intent } = started;
  let { sessionId } = started;
  let questionChangedCount = 0;
  let failureReason: string | null = null;
  let streamConsumed = false;
  const generations = createAppliedGenerationCounter();

  try {
    for await (const event of events) {
      if (event.type === 'setSessionId') sessionId = event.sessionId;

      // 소비자가 이벤트 적용을 끝낸 뒤에만 성공 지표를 기록한다. yield 중 소비자가
      // throw 하면 generator 는 return 되어 아래 성공 기록까지 진행하지 않는다.
      yield event;

      const recorded = trackTurnStreamEvent(event, {
        sessionId,
        clientRequestId,
        draftId: request.context.draftInstanceId,
        generations,
      });
      questionChangedCount += recorded.changedCount;
      if (recorded.failureReason) failureReason = recorded.failureReason;
    }
    streamConsumed = true;
  } catch (error) {
    failureReason = aborted() ? 'aborted' : 'stream_error';
    throw error;
  } finally {
    // draft 적용이 실제로 일어난 시점 기준의 "생성 완료" — 스트림이 끝난 뒤 1회.
    generations.track({
      place: 'ai_builder_chat',
      promptType: request.context.sourceRefs.length > 0 ? 'source' : 'query',
      sessionId,
      clientRequestId,
    });
    trackTurnResult(
      resolveTurnFailureReason({
        failureReason,
        aborted: aborted(),
        streamConsumed,
      }),
      {
        intent,
        sessionId,
        clientRequestId,
        latencyMs: Date.now() - startedAt,
        questionChangedCount,
      },
    );
  }
}

/**
 * 로그인 만료 감지 1회당 1회. 발화 책임은 만료 단일 진입점
 * (`handleAiBuilderAuthExpired`)에 있다 — 중복 가드가 거기 있기 때문이다.
 */
export const trackAiBuilderAuthExpired = (params: {
  place: AiBuilderAuthExpiredPlace;
  sessionId: string;
  messageCount: number;
  hadActiveTurn: boolean;
  snapshotSkipReason: AiBuilderChatSnapshotSkipReason;
}) => {
  trackEvent('quiz_ai_builder_auth_expired_view', {
    place: params.place,
    session_id: params.sessionId,
    message_count: params.messageCount,
    had_active_turn: params.hadActiveTurn,
    snapshot_saved: params.snapshotSkipReason === '',
    snapshot_skip_reason: params.snapshotSkipReason,
  });
};

export const trackAiBuilderChatRestore = (params: {
  result: AiBuilderChatRestoreResult;
  sessionId: string;
  messageCount: number;
  sourceCount: number;
  ageMs: number;
}) => {
  trackEvent('quiz_ai_builder_chat_restore', {
    result: params.result,
    session_id: params.sessionId,
    message_count: params.messageCount,
    source_count: params.sourceCount,
    age_ms: params.ageMs,
  });
};

export const trackAiBuilderUsageBlocked = (
  usage: AiBuilderUsageInfo | null,
  place: AiBuilderUsageBlockedPlace,
) => {
  trackEvent('quiz_ai_builder_usage_limit_view', {
    place,
    remaining: usage?.remaining ?? 0,
    limit: usage?.limit ?? -1,
  });
};
